import { useAppSelector } from "@/redux/hooks";
import BackControls from "../components/BackControls";
import {
    ArrowLeft,
    ArrowRight,
    Search,
    Atom,
    BarChart3,
    Binary,
    BookOpen,
    Brain,
    BriefcaseMedical,
    Building2,
    Calculator,
    Cpu,
    Droplets,
    Factory,
    FileText,
    FlaskConical,
    Flower2,
    Fuel,
    Globe2,
    GraduationCap,
    HardHat,
    HeartPulse,
    History,
    Info,
    Landmark,
    Languages,
    Leaf,
    type LucideIcon,
    MapPin,
    Megaphone,
    Microscope,
    Mountain,
    PaintBucket,
    Palette,
    PenLine,
    Pill,
    Scale,
    Scroll,
    ShieldCheck,
    Soup,
    Speaker,
    Stethoscope,
    TestTubes,
    TrendingUp,
    Users,
    Waves,
    Wheat,
    Zap,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { College, Department, Gender, Track } from "../data/sharedTypes";
import { getCollegeNameKey } from "../data/sharedTypes";
import type { UniversityId } from "../data/universityRegistry";
import { getCollegeSpecialties, getDisplayedMinRate, getSpecialtyInfoAlert, getTrackMinHighSchool, getUniversityInfo, isFlatStructure } from "../data/universityRegistry";
import StepHeader from "../StepHeader";

const specialtyIcons: Record<string, LucideIcon> = {
    // Engineering & Petroleum
    industrial_engineering: Factory,
    mechanical_engineering: HardHat,
    electrical_engineering: Zap,
    chemical_engineering: FlaskConical,
    petroleum_engineering: Fuel,
    civil_engineering: Building2,
    computer_engineering: Cpu,

    // Architecture
    interior_design: PaintBucket,
    visual_design: Palette,
    architecture: Landmark,

    // Arts
    arabic_language: PenLine,
    french_language: Languages,
    history: History,
    philosophy: BookOpen,
    media: Megaphone,

    // Arts - English
    english_language: Languages,

    // Sharia
    fiqh_usul: Scale,
    comparative_fiqh: Scroll,
    tafsir_hadith: BookOpen,
    aqeedah_dawah: Speaker,

    // Social Sciences
    sociology: Users,
    social_work: HeartPulse,
    psychology: Brain,
    political_science: Landmark,
    applied_geography: MapPin,
    geographic_info: Globe2,

    // Administrative Sciences
    accounting: Calculator,
    management: BriefcaseMedical,
    marketing: TrendingUp,
    finance: BarChart3,
    economics: TrendingUp,
    mis: Binary,
    operations: Factory,
    public_admin: Building2,

    // Education - Literary
    islamic_studies: Scroll,
    geography: Globe2,
    kindergarten: GraduationCap,
    primary_arabic: PenLine,
    primary_english: Languages,
    primary_islamic: Scroll,
    primary_social: Users,

    // Education - Scientific
    primary_science: Microscope,
    primary_math: Calculator,
    physics: Atom,
    chemistry: FlaskConical,
    biology: Leaf,
    geology: Mountain,
    math: Calculator,

    // Science
    animal_biology: Stethoscope,
    plant_biology: Flower2,
    microbiology: Microscope,
    biochemistry: TestTubes,
    molecular_biology: Microscope,
    mathematics: Calculator,
    chemistry_dept: FlaskConical,
    applied_chemistry: FlaskConical,
    basic_physics: Atom,
    engineering_physics: Atom,
    cybersecurity: ShieldCheck,
    financial_math: Calculator,
    laser_telecom: Zap,
    marine_science: Waves,
    statistics: BarChart3,
    applied_statistics: BarChart3,
    computer_science: Cpu,

    // Life Sciences
    data_science_ai: Brain,
    food_science: Soup,
    nutrition: Wheat,
    communication_disorders: Speaker,
    environmental_science: Leaf,
    information_science: FileText,

    // Allied Medical Sciences
    diagnostic_radiology: Droplets,
    nursing: HeartPulse,
    occupational_therapy: HeartPulse,
    physical_therapy: HeartPulse,
    medical_lab: TestTubes,
    health_info: FileText,

    // AASU
    supply_chain_logistics: Factory,
    digital_marketing: TrendingUp,
    entrepreneurship_innovation: Landmark,
    cybersecurity_engineering: ShieldCheck,
    software_engineering: Cpu,
    computer_systems_engineering: Cpu,
    robotics_mechatronics: HardHat,
    energy_systems: Zap,
    biomedical_engineering: Stethoscope,
    materials_engineering: FlaskConical,
    agricultural_engineering: Wheat,

    // PAAET
    educational_technology: Cpu,
    english_middle_school: Languages,
    arts_education: Palette,
    music_education: Speaker,
    information_studies: FileText,
    sciences: Microscope,
    computer_teacher: Cpu,
    electricity: Zap,
    real_estate_finance: TrendingUp,
    nutrition_sciences: Wheat,
    fire_engineering: ShieldCheck,
    municipal_cleanliness_inspection: ShieldCheck,
    phlebotomy: Stethoscope,
    health_education: HeartPulse,
    sterilization: Stethoscope,
    kindergarten_f: GraduationCap,
    home_economics_f: Soup,
    physical_education_f: HeartPulse,
    environmental_sciences_f: Leaf,
    oral_dental_health_f: Stethoscope,
    anesthesia_technician: Pill,
    pharmaceutical_sciences: Pill,
    respiratory_therapist: HeartPulse,
    medical_lab_technology: TestTubes,
    dental_clinic_technician_f: Stethoscope,
    emergency_medical_tech: HeartPulse,
    food_nutrition_sciences: Wheat,
    medical_records: FileText,
    industrial_health_safety_m: ShieldCheck,
    school_nursing: HeartPulse,
    english_primary_f: Languages,
    applied_physics_labs_f: Atom,
    school_nursing_bsc: HeartPulse,
    nursing_bsc: HeartPulse,
    nursing_diploma: HeartPulse,
    insurance: FileText,
    banking: TrendingUp,
    computer_science_s: Cpu,
    computer_science_l: Cpu,
    law: Scale,
    applied_statistics_l: BarChart3,
    admin_info_systems: Binary,
    logistics: Factory,
    human_resources: Users,
    financial_management: TrendingUp,
    chemical_engineering_bsc: FlaskConical,
    chemical_engineering_comp: FlaskConical,
    mechanical_power: HardHat,
    manufacturing_technology: Factory,
    automotive_mechanics: HardHat,
    hvac_mechanics: HardHat,
    marine_engineering_mechanics: Waves,
    welding_technology: HardHat,
    electrical_machines: Zap,
    power_distribution: Zap,
    renewable_energy_tech: Zap,
    telecom_engineering: Cpu,
    industrial_electronics: Zap,
    biomedical_equipment: Stethoscope,
    building_construction: Building2,
    roads: HardHat,
    surveying: MapPin,
    refinery_operations: Fuel,
    chemical_industries: FlaskConical,
    applied_chemistry_labs: FlaskConical,
    petroleum_exploration: Fuel,
    petroleum_production: Fuel,
    energy_efficiency: Zap,
    labor_inspection: ShieldCheck,
    science_lab_prep: Microscope,
    quran_memorization_l: BookOpen,
    agriculture: Wheat,
    veterinary: Stethoscope,
    telecom_navigation_s: Cpu,
    telecom_navigation_l: Cpu,
    energy_s: Zap,
    energy_l: Zap,
    construction_prep: Building2,
    vehicle_inspection_s: ShieldCheck,
    technician_s: HardHat,
    applied_arts_design: Palette,
    social_services: Users,
    secretarial: FileText,
    admin_services_dept: Building2,
    hotel_services: Building2,
    transaction_processing: FileText,
    art_inspector: Palette,
};

interface Props {
    universityId: UniversityId;
    college: College;
    selectedTrack?: Track | null;
    selectedGender?: Gender | null;
    onBack: () => void;
    onSelectSpecialty: (department: Department) => void;
}

export default function ChooseSpecialty({
    universityId,
    college,
    selectedTrack,
    selectedGender,
    onBack,
    onSelectSpecialty,
}: Props) {
    const { t } = useTranslation();
    const { locale } = useAppSelector((state) => state);
    const specialties = getCollegeSpecialties(universityId, college.key, selectedGender ?? undefined, selectedTrack);
    const isFlat = isFlatStructure(universityId);
    const universityInfo = getUniversityInfo(universityId);
    const trackMinHs = getTrackMinHighSchool(universityId, selectedTrack);
    // KU Science & Law present the track-min as the apply-floor and the
    // specialty's rate as last year's accepted figure (competitive target).
    const isKuSciLaw =
        universityId === "kuwait_university" && (college.key === "science" || college.key === "law");
    const [searchQuery, setSearchQuery] = useState("");

    const getDeptName = (deptKey: string | null) => {
        if (!deptKey) return "";
        if (universityId === "internal_missions") {
            return t(`dream_university_institution_${deptKey}`);
        }
        if (isFlat) return t(`dream_university_country_${deptKey}`);
        return t(`dream_university_dept_${deptKey}`);
    };

    const filteredSpecialties = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return specialties;
        return specialties.filter((dept) =>
            getDeptName(dept.departmentKey).toLowerCase().includes(q),
        );
    }, [specialties, searchQuery, t, isFlat]);

    return (
        <div className="flex flex-col gap-y-6">
            <BackControls onBack={onBack} />

            <StepHeader
                title={t("dream_university_choose_specialty")}
                subtitle={`${college.emoji} ${t(getCollegeNameKey(college))}`}
            />

            {/* Selection trail */}
            <div className="flex flex-wrap items-center justify-center gap-2">
                {universityInfo && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-gray-100 text-gray-700">
                        <span>{universityInfo.emoji}</span>
                        {t(universityInfo.nameKey)}
                    </span>
                )}
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-theme-50 text-theme-700 ring-1 ring-theme-200">
                    <span>{college.emoji}</span>
                    {t(getCollegeNameKey(college))}
                </span>
                {selectedTrack && trackMinHs !== null && (
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${
                        selectedTrack === "scientific"
                            ? "bg-blue-50 text-blue-700 ring-1 ring-blue-200"
                            : "bg-purple-50 text-purple-700 ring-1 ring-purple-200"
                    }`}>
                        {t(`dream_university_track_${selectedTrack}`)} · {t("dream_university_track_min_hs", { percent: trackMinHs })}
                    </span>
                )}
            </div>

            {(() => {
                const result = getSpecialtyInfoAlert(universityId, college, selectedGender ?? undefined);
                if (!result) {
                    return null;
                }
                const alerts = Array.isArray(result) ? result : [result];
                return (
                    <div className="flex flex-col gap-y-3">
                        {alerts.map((alert, idx) => (
                            <div key={idx} className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4">
                                <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                                <div className="space-y-1">
                                    {alert.titleKey && (
                                        <p className="text-sm font-semibold text-blue-700">{t(alert.titleKey)}</p>
                                    )}
                                    {alert.lines.map((line, i) => (
                                        <p key={i} className="text-sm text-blue-700">{t(line.key, line.params)}</p>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                );
            })()}

            {specialties.length > 4 && (
                <div className="relative">
                    <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t("dream_university_search_specialty")}
                        className="w-full ps-9 pe-3 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-theme-500 focus:border-transparent placeholder:text-gray-400"
                    />
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {filteredSpecialties.map((dept) => {
                    const Icon =
                        specialtyIcons[dept.departmentKey || ""] ||
                        GraduationCap;
                    return (
                        <button
                            key={dept.departmentKey}
                            onClick={() => onSelectSpecialty(dept)}
                            className="flex items-start gap-3 p-4 bg-white border border-gray-200 rounded-xl hover:border-theme-500 hover:shadow-md transition-all duration-200 text-start"
                        >
                            <div className="w-9 h-9 shrink-0 bg-theme-100 rounded-lg flex items-center justify-center">
                                <Icon className="w-4.5 h-4.5 text-theme-700" />
                            </div>
                            <div className="flex flex-col gap-0.5">
                                <span className="text-sm font-medium text-gray-800">
                                    {getDeptName(dept.departmentKey)}
                                    {dept.stage && (
                                        <span className="ms-1 text-xs font-normal text-gray-500">
                                            – {t(`dream_university_stage_${dept.stage}`)}
                                        </span>
                                    )}
                                </span>
                                {dept.type === "high_school" ? (
                                    trackMinHs !== null ? (
                                        <>
                                            <span className="text-xs text-amber-600">
                                                {isKuSciLaw
                                                    ? t("dream_university_ku_specialty_min_hs_to_apply", { rate: trackMinHs })
                                                    : t("dream_university_specialty_min_hs", { rate: trackMinHs })}
                                            </span>
                                            <span className="text-xs text-gray-500">
                                                {isKuSciLaw
                                                    ? t("dream_university_ku_specialty_last_year_accepted", { rate: dept.equivalentRate })
                                                    : t("dream_university_specialty_min_hs_rate", { rate: dept.equivalentRate })}
                                            </span>
                                        </>
                                    ) : universityId === "paaet" ? (
                                        <>
                                            {dept.passOnly ? (
                                                <span className="text-xs text-amber-600">
                                                    {t("dream_university_specialty_just_pass_hs")}
                                                </span>
                                            ) : dept.minHighSchool !== undefined ? (
                                                <span className="text-xs text-amber-600">
                                                    {t("dream_university_paaet_specialty_min_hs_to_apply", { rate: dept.minHighSchool })}
                                                </span>
                                            ) : null}
                                            {dept.equivalentRate > 0 && (
                                                <span className="text-xs text-gray-500">
                                                    {t("dream_university_specialty_last_year_accepted_rate", { rate: dept.equivalentRate })}
                                                </span>
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            {dept.equivalentRate > 0 && (
                                                <span className="text-xs text-amber-600">
                                                    {t("dream_university_specialty_min_hs_rate", { rate: dept.equivalentRate })}
                                                </span>
                                            )}
                                        </>
                                    )
                                ) : universityId === "abdullah_al_salem" ? (
                                    (() => {
                                        // AASU specialty cards (BA / Computing / Engineering —
                                        // Medicine has no specialty step). Displayed value
                                        // is dispatched through the registry so AASU's
                                        // last-year-accepted logic stays in its own module.
                                        // S1 2026/2027 published أقل معدل مكافئ per specialty.
                                        const minAccepted = getDisplayedMinRate(
                                            universityId,
                                            dept,
                                            selectedTrack,
                                            college.weights,
                                            dept.equivalentRate,
                                        );
                                        if (!minAccepted) return null;
                                        return (
                                            <span className="text-xs text-emerald-700">
                                                {t("dream_university_aasu_specialty_last_year_equivalent_rate", { rate: minAccepted })}
                                            </span>
                                        );
                                    })()
                                ) : (
                                    <>
                                        {dept.minHighSchool && (
                                            <span className="text-xs text-amber-600">
                                                {t("dream_university_specialty_min_hs", { rate: dept.minHighSchool })}
                                            </span>
                                        )}
                                        <span className="text-xs text-gray-500">
                                            {t("dream_university_specialty_min_rate", { rate: dept.equivalentRate })}
                                        </span>
                                    </>
                                )}
                                {dept.additionalConditions && (
                                    <ul className="mt-1 flex flex-col gap-1">
                                        {(Array.isArray(dept.additionalConditions)
                                            ? dept.additionalConditions
                                            : [dept.additionalConditions]
                                        ).map((condition) => (
                                            <li
                                                key={condition}
                                                className="flex items-start gap-1.5 text-xs text-amber-600"
                                            >
                                                <span
                                                    aria-hidden="true"
                                                    className="mt-[5px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                                                />
                                                <span>{t(`dream_university_${condition}`)}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
